/**
 * The admin-authored rich text shared by a request type's instructions and by an
 * info block's body. They are the same value in two places, so the limit, the
 * allow-list and these readers live in one module — built twice, they would
 * drift on the first bug fix.
 *
 * Deliberately DOM-free: these run during a save, inside unit tests, and on the
 * server.
 */

/** Core's cap on `instructions` and on an `info_block` field's `description`. */
export const WORKPLACE_REQUEST_RICH_TEXT_MAX_LENGTH = 20_000;

/**
 * The only tags kept, on save and again on render. Anything else the editor or a
 * paste produces is stripped, so what an admin sees while writing is what an
 * employee gets.
 */
export const RICH_TEXT_ALLOWED_TAGS = [
  "p",
  "strong",
  "em",
  "u",
  "ul",
  "ol",
  "li",
  "a",
  "br"
] as const;

/**
 * Tags that don't break the flow of a sentence. They vanish; everything else
 * leaves a space behind, so two list items don't run together into one word
 * while `<strong>Fooda</strong>.` still keeps its full stop attached.
 */
const INLINE_TAGS = new Set([
  "a",
  "abbr",
  "b",
  "cite",
  "code",
  "em",
  "i",
  "kbd",
  "mark",
  "q",
  "s",
  "small",
  "span",
  "strong",
  "sub",
  "sup",
  "time",
  "u",
  "var"
]);

const NAMED_ENTITIES: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'",
  "&nbsp;": " "
};

/**
 * The body's text, with the markup taken out — for row summaries, emptiness
 * checks and anywhere a body has to be read as a plain string.
 */
export const richTextToPlainText = (
  html: string | null | undefined
): string => {
  if (!html) return "";
  return html
    .replace(/<\/?([a-zA-Z][a-zA-Z0-9-]*)[^>]*>/g, (_tag, name: string) =>
      INLINE_TAGS.has(name.toLowerCase()) ? "" : " "
    )
    .replace(/&[a-zA-Z#0-9]+;/g, (entity) => NAMED_ENTITIES[entity] ?? " ")
    .replace(/\s+/g, " ")
    .trim();
};

const HTML_TAG = /<[a-zA-Z][^>]*>/;

const escapeHtml = (text: string): string =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * The value as HTML, whichever way it was stored.
 *
 * A body that holds no tag at all is plain text — notes written before the
 * editor was rich text, or a reservation description a workflow resolved from
 * form answers — and would lose every newline the moment an HTML surface
 * parsed it, since a newline is only whitespace there. It comes back escaped,
 * with a break per newline, in one paragraph. Anything holding a tag is
 * already HTML and passes through untouched.
 *
 * Only the reading side converts: what is stored stays as it was until
 * someone edits it.
 */
export const toRichTextHtml = (value: string | null | undefined): string => {
  if (!value) return "";
  if (HTML_TAG.test(value)) return value;
  return `<p>${escapeHtml(value).replace(/\r?\n/g, "<br>")}</p>`;
};

/**
 * Whether the body would render as nothing.
 *
 * Emptying the editor leaves `<p></p>` or `<p><br></p>` behind rather than an
 * empty string, so a plain falsiness check would report a blank body as filled
 * in — and a save would then store the empty paragraph instead of clearing the
 * value.
 */
export const isRichTextEmpty = (html: string | null | undefined): boolean =>
  richTextToPlainText(html).length === 0;

/**
 * A one-line preview of the body, for the collapsed field row. Truncates on a
 * word boundary where it can, so the summary doesn't cut mid-word.
 */
export const summarizeRichText = (
  html: string | null | undefined,
  maxLength = 68
): string => {
  const text = richTextToPlainText(html);
  if (text.length <= maxLength) return text;
  const clipped = text.slice(0, maxLength);
  const lastSpace = clipped.lastIndexOf(" ");
  return `${(lastSpace > maxLength / 2 ? clipped.slice(0, lastSpace) : clipped).trimEnd()}…`;
};
